/**
 * 表格 (组合式):
 *   <Table>
 *     <TableHeader><TableRow><TableHead>名称</TableHead><TableHead numeric>金额</TableHead></TableRow></TableHeader>
 *     <TableBody>
 *       {rows.length === 0 ? <TableEmpty colSpan={2} title="暂无数据" /> : rows.map(…<TableRow><TableCell/>…)}
 *     </TableBody>
 *   </Table>
 * numeric: 右对齐 + tabular-nums。Table 外层自带卡片边框与横向滚动 (bare 去掉卡片外观)。
 */
import type { HTMLAttributes, ReactNode, TdHTMLAttributes, ThHTMLAttributes } from "react";
import type { LucideIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import { EmptyState } from "./EmptyState";

export function Table({
  className,
  wrapperClassName,
  bare,
  ...props
}: HTMLAttributes<HTMLTableElement> & { wrapperClassName?: string; bare?: boolean }) {
  return (
    <div
      className={cn(
        "w-full overflow-x-auto",
        !bare && "rounded-xl border bg-card shadow-sm",
        wrapperClassName,
      )}
    >
      <table className={cn("w-full caption-bottom border-collapse text-sm", className)} {...props} />
    </div>
  );
}

export function TableHeader({ className, ...props }: HTMLAttributes<HTMLTableSectionElement>) {
  return <thead className={cn("bg-muted/60 [&_tr]:border-b", className)} {...props} />;
}

export function TableBody({ className, ...props }: HTMLAttributes<HTMLTableSectionElement>) {
  return <tbody className={cn("[&_tr:last-child]:border-0", className)} {...props} />;
}

export function TableFooter({ className, ...props }: HTMLAttributes<HTMLTableSectionElement>) {
  return <tfoot className={cn("border-t bg-muted/40 font-medium", className)} {...props} />;
}

export function TableRow({ className, ...props }: HTMLAttributes<HTMLTableRowElement>) {
  return <tr className={cn("border-b transition-colors hover:bg-muted/40", className)} {...props} />;
}

export function TableHead({
  className,
  numeric,
  ...props
}: ThHTMLAttributes<HTMLTableCellElement> & { numeric?: boolean }) {
  return (
    <th
      scope="col"
      className={cn(
        "h-10 whitespace-nowrap px-3 text-left align-middle text-xs font-medium text-muted-foreground",
        numeric && "text-right",
        className,
      )}
      {...props}
    />
  );
}

export function TableCell({
  className,
  numeric,
  ...props
}: TdHTMLAttributes<HTMLTableCellElement> & { numeric?: boolean }) {
  return (
    <td
      className={cn("px-3 py-2.5 align-middle", numeric && "whitespace-nowrap text-right tabular-nums", className)}
      {...props}
    />
  );
}

/** 表格内的空状态行 */
export function TableEmpty({
  colSpan,
  title = "暂无数据",
  hint,
  icon,
  action,
}: {
  colSpan: number;
  title?: ReactNode;
  hint?: ReactNode;
  icon?: LucideIcon;
  action?: ReactNode;
}) {
  return (
    <tr className="hover:bg-transparent">
      <td colSpan={colSpan}>
        <EmptyState compact title={title} hint={hint} icon={icon} action={action} />
      </td>
    </tr>
  );
}
